/**
 * registration.ts — the vehicle's registration, read and written in one place.
 *
 * THE WRITE IS `RegistrationForm`'S, MOVED RATHER THAN REWRITTEN: the same bucket, the same
 * `certificates/<uuid>.<ext>` path, the same public URL, the same insert-then-point-the-vehicle-at-
 * it order. It has to be the same, because the sheet is still mounted on `ContractInsightDocuments`
 * and two write paths for one row is how the two surfaces end up disagreeing about what a
 * registration is. The sheet now calls this; the setup canvas's inline group calls this; there is
 * one body.
 *
 * THE VEHICLE POINTS AT THE REGISTRATION, NOT THE OTHER WAY ROUND. `vehicles.registration_id →
 * vehicle_registrations.id` is the relationship (never `vr.id = v.id`, which has caused mis-join
 * bugs in this codebase before), which is why creating a registration is two writes and why the
 * second one cannot be skipped: a `vehicle_registrations` row nothing points at is invisible to
 * every reader including `get_preparation_status`.
 */

import { supabase } from '@/integrations/supabase/client';
import type { VehicleRegistrationType } from '@/lib/registration';

import { validateRegistrationDraft, type RegistrationDraft } from './registrationDraft';

export const vehicleRegistrationKey = (vehicleId: number) =>
    ['vehicle-registration', vehicleId] as const;

export interface VehicleRegistration {
    id: number;
    type: string | null;
    number: string | null;
    certificate_url: string | null;
    transaction_id: number | null;
}

/** The registration the vehicle points at, or null. Same select the sheet has always used. */
export async function fetchVehicleRegistration(
    vehicleId: number,
): Promise<VehicleRegistration | null> {
    const { data, error } = await supabase
        .from('vehicles')
        .select(`
            registration_id,
            vehicle_registrations!vehicles_registration_id_fkey(
                id,
                type,
                number,
                certificate_url,
                transaction_id,
                transactions(amount)
            )
        `)
        .eq('id', vehicleId)
        .single();
    if (error) throw error;
    return (data?.vehicle_registrations as unknown as VehicleRegistration) || null;
}

/**
 * Create or amend the registration, and upload its certificate if one was staged.
 *
 * THE UPLOAD RUNS FIRST AND ITS FAILURE STOPS THE WRITE, which is the order the sheet used: a row
 * saved with a certificate that never landed is worse than a save that was refused, because
 * nothing on any surface would ever say the document is missing.
 */
export async function saveVehicleRegistration({
    vehicleId,
    registrationId,
    draft,
    certificateFile,
}: {
    vehicleId: number;
    /** The row to amend; null creates one and points the vehicle at it. */
    registrationId: number | null;
    draft: RegistrationDraft;
    certificateFile: File | null;
}): Promise<void> {
    const refusal = validateRegistrationDraft(draft);
    if (refusal) throw new Error(refusal);

    let certificateUrl: string | null = null;
    if (certificateFile) {
        const fileExt = certificateFile.name.split('.').pop();
        const fileName = `certificates/${crypto.randomUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
            .from('vehicle-registrations')
            .upload(fileName, certificateFile);
        if (uploadError) throw uploadError;
        certificateUrl = supabase.storage.from('vehicle-registrations').getPublicUrl(fileName).data
            .publicUrl;
    }

    const type = draft.type as VehicleRegistrationType;
    const number = draft.number.trim();
    const transactionId = Number(draft.transactionId);

    if (registrationId != null) {
        const updateData: Record<string, unknown> = { type, number, transaction_id: transactionId };
        // Only when one was staged: an unchanged certificate must not be overwritten with null.
        if (certificateUrl) updateData.certificate_url = certificateUrl;

        const { error } = await supabase
            .from('vehicle_registrations')
            .update(updateData)
            .eq('id', registrationId);
        if (error) throw error;
        return;
    }

    const { data: created, error: insertError } = await supabase
        .from('vehicle_registrations')
        .insert({ type, number, transaction_id: transactionId, certificate_url: certificateUrl })
        .select('id')
        .single();
    if (insertError) throw insertError;

    const { error: vehicleError } = await supabase
        .from('vehicles')
        .update({ registration_id: created.id })
        .eq('id', vehicleId);
    if (vehicleError) throw vehicleError;
}
